import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { handleCallback } from './auth/authService';
import ProtectedRoute from './auth/ProtectedRoute';
import NavBar from './Components/NavBar/NavBar';
import Landing from './Components/Landing/Landing';
import Restricted from './Components/Restricted/Restricted';
import BugReportButton from './Components/BugReport/BugReportButton';
import AutoBugReporter from './Components/BugReport/AutoBugReporter';
import { AccessSignalsProvider } from './services/accessSignals';
import { logPageView } from './services/auditService';
import { installWorkInteractionTracking, noteRoute } from './services/workTelemetry';
import { getUser } from './auth/authService';
import { isCostUser } from './auth/costAccess';
import { clearOnSignOut } from './auth/signOutCleanup';
import './App.css';

// Every page is its own chunk (speed audit, 2026-10-19): the first load pulls the shell
// and the page you opened, not the admin console, the report editor and Cost of work.
// A chunk arrives in well under a second on the office connection; the fallback is a
// blank page area, not a spinner, so nothing flashes on the common case.
const CS = lazy(() => import('./Components/CS/ClientOverview/ClientOverview'));
const Implementations = lazy(() => import('./Components/Implementations/Implementations'));
const ClientHealth = lazy(() => import('./Components/ClientHealth/ClientHealth'));
const ClientWins = lazy(() => import('./Components/ClientWins/ClientWins'));
const Account = lazy(() => import('./Components/Account/Account'));
const Settings = lazy(() => import('./Components/Settings/Settings'));
const Help = lazy(() => import('./Components/Help/Help'));
const WhatsNew = lazy(() => import('./Components/WhatsNew/WhatsNew'));
const IntegrationsWhatsNew = lazy(() => import('./Components/WhatsNew/IntegrationsWhatsNew'));
const Admin = lazy(() => import('./Components/Admin/Admin'));
const AgentChat = lazy(() => import('./Components/AgentChat/AgentChat'));
const Modularities = lazy(() => import('./Components/Admin/Modularities'));
const HaikuSeat = lazy(() => import('./Components/Admin/HaikuSeat'));
const DistrictLooks = lazy(() => import('./Components/Admin/DistrictLooks'));
const Permissions = lazy(() => import('./Components/Admin/Permissions'));
const AdminInsights = lazy(() => import('./Components/Admin/Insights'));
const AdminShortcuts = lazy(() => import('./Components/Admin/Shortcuts'));
const CsDigest = lazy(() => import('./Components/Admin/CsDigest'));
const WorkSettings = lazy(() => import('./Components/Admin/WorkSettings'));
const HelpVideos = lazy(() => import('./Components/Admin/HelpVideos'));
const FileFollowUp = lazy(() => import('./Components/CS/FileFollowUp/FileFollowUp'));
const DistrictReport = lazy(() => import('./Components/CS/DistrictReport/DistrictReport'));
const WorkOverview = lazy(() => import('./Components/Work/WorkOverview'));
const CostOfWork = lazy(() => import('./Components/Work/CostOfWork'));
const CostReview = lazy(() => import('./Components/Work/CostReview'));
const RoadmapTidy = lazy(() => import('./Components/Work/RoadmapTidy'));
const Roadmap = lazy(() => import('./Components/Roadmap/Roadmap'));
const ReadyForMe = lazy(() => import('./Components/Work/ReadyForMe'));
const MeetingsCalendar = lazy(() => import('./Components/Meetings/MeetingsCalendar'));
const AdminAssistant = lazy(() => import('./Components/Admin/Assistant'));
const Finance = lazy(() => import('./Components/Finance/Finance'));
const DataConfig = lazy(() => import('./Components/DataConfig/DataConfig'));
const AlertsConsole = lazy(() => import('./Components/Admin/AlertsConsole'));
const ChatCostAlerts = lazy(() => import('./Components/Admin/ChatCostAlerts'));
const DatasetTags = lazy(() => import('./Components/Implementations/DatasetTags/DatasetTags'));

// Cost of work is for the finance group only (auth/costAccess.js). Anyone
// else who lands on its URL is sent to the Work overview rather than shown a
// page they were not meant to see. The chip that leads here is hidden by the
// same rule, so this is the belt to that braces.
const CostOnly = ({ children }) => {
    const [allowed, setAllowed] = React.useState(null);
    useEffect(() => {
        getUser().then((u) => setAllowed(isCostUser(u?.profile))).catch(() => setAllowed(false));
    }, []);
    if (allowed === null) return null;
    return allowed ? children : <Navigate to="/work" replace />;
};

const PageViewLogger = () => {
    const location = useLocation();
    useEffect(() => installWorkInteractionTracking(), []);
    useEffect(() => {
        logPageView(location.pathname + location.search);
        noteRoute(location.pathname + location.search);
    }, [location.pathname, location.search]);
    return null;
};

const LoginCallback = () => {
    useEffect(() => {
        const processCallback = async () => {
            try {
                const user = await handleCallback();
                const returnTo = user?.state?.returnTo || '/';
                window.location.replace(returnTo);
            } catch (error) {
                console.error('Login callback error:', error);
                window.location.replace('/');
            }
        };
        processCallback();
    }, []);

    return <div />;
};

const LogoutCallback = () => {
    useEffect(() => {
        clearOnSignOut();
        window.location.replace('/');
    }, []);
    return <div />;
};

const App = () => {
    return (
        <div className="app-container">
            <Routes>
                <Route path="/signin-oidc" element={<LoginCallback />} />
                <Route path="/signout-callback-oidc" element={<LogoutCallback />} />
                <Route path="/restricted" element={<Restricted />} />
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute>
                            <NavBar />
                            <PageViewLogger />
                            <Suspense fallback={null}><AgentChat /></Suspense>
                            <BugReportButton />
                            <AutoBugReporter />
                            <AccessSignalsProvider>
                            <div className="page-content">
                                <Suspense fallback={<div className="page-loading" aria-busy="true" />}>
                                <Routes>
                                    <Route path="/" element={<Landing />} />
                                    <Route path="/cs" element={<CS />} />
                                    <Route path="/cs/file" element={<FileFollowUp />} />
                                    <Route path="/cs/district-report" element={<DistrictReport />} />
                                    <Route path="/work" element={<WorkOverview />} />
                                    <Route path="/work/cost" element={<CostOnly><CostReview /></CostOnly>} />
                                    <Route path="/work/roadmap-tidy" element={<RoadmapTidy />} />
                                    <Route path="/roadmap" element={<Roadmap />} />
                                    <Route path="/work/ready" element={<ReadyForMe />} />
                                    <Route path="/work/cost/detail" element={<CostOnly><CostOfWork /></CostOnly>} />
                                    <Route path="/meetings" element={<MeetingsCalendar />} />
                                    <Route path="/implementations" element={<Implementations />} />
                                    <Route path="/implementations/dataset-tags/*" element={<DatasetTags />} />
                                    <Route path="/data-config/*" element={<DataConfig />} />
                                    <Route path="/client-health" element={<ClientHealth />} />
                                    <Route path="/client-wins" element={<ClientWins />} />
                                    <Route path="/account" element={<Account />} />
                                    <Route path="/admin" element={<Admin/>} />
                                    <Route path="/admin/modularities" element={<Modularities/>} />
                                    <Route path="/admin/efficient-chat" element={<HaikuSeat/>} />
                                    <Route path="/admin/district-looks" element={<DistrictLooks/>} />
                                    <Route path="/admin/permissions" element={<Permissions/>} />
                                    <Route path="/admin/insights" element={<AdminInsights/>} />
                                    <Route path="/admin/shortcuts" element={<AdminShortcuts/>} />
                                    <Route path="/admin/cs-digest" element={<CsDigest/>} />
                                    <Route path="/admin/work-settings" element={<WorkSettings/>} />
                                    <Route path="/admin/help-videos" element={<HelpVideos/>} />
                                    <Route path="/admin/assistant" element={<AdminAssistant/>} />
                                    <Route path="/admin/alerts" element={<AlertsConsole/>} />
                                    <Route path="/admin/chat-cost-alerts" element={<ChatCostAlerts/>} />
                                    <Route path="/settings" element={<Settings />} />
                                    <Route path="/help" element={<Help />} />
                                    <Route path="/whats-new" element={<WhatsNew />} />
                                    <Route path="/integrations/whats-new" element={<IntegrationsWhatsNew />} />
                                    <Route path="/finance" element={<Finance />} />
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                                </Suspense>
                            </div>
                            </AccessSignalsProvider>
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </div>
    );
};

export default App;
